(function () {
  if (window.threeDMarkAddonCartGuardLoaded) return;
  window.threeDMarkAddonCartGuardLoaded = true;

  // Shared by cart-items.js and the addon guard; never monkey-patches window.fetch.
  let queue = Promise.resolve();
  let pending = 0;
  let failure = null;
  let checkingOut = false;
  let allowSubmit = null;
  let submittedEvent = null;
  let reviewRequired = false;
  const messages = {};
  const busyElements = new Map();

  function message(kind) {
    const source = document.querySelector('cart-items');
    if (source) {
      ['updated', 'failed', 'pack'].forEach((key) => {
        messages[key] = source.dataset[`safety${key[0].toUpperCase()}${key.slice(1)}`];
      });
    }
    return messages[kind] || (window.theme && theme.strings.cartError) || '';
  }

  function showMessage(kind) {
    let box = document.getElementById('3dmark-cart-safety-message');
    if (!box) {
      box = document.createElement('div');
      box.id = '3dmark-cart-safety-message';
      box.className = 'alert mt-6 mb-6 bg-error-bg text-error-text';
      box.setAttribute('role', 'alert');
      const host = document.querySelector('cart-items');
      if (!host) return;
      host.before(box);
    }
    box.textContent = message(kind);
    box.hidden = false;
  }

  function fail(error) {
    failure = error || new Error(message('failed'));
    showMessage(error && error.cartSafetyKind === 'pack' ? 'pack' : 'failed');
    console.warn('[3DMART cart safety]', error);
  }

  function setBusy() {
    const busy = pending > 0 || checkingOut;
    if (busy) {
      document.querySelectorAll('cart-items, .dynamic-cart-btns').forEach((element) => {
        if (!busyElements.has(element)) busyElements.set(element, element.inert);
        element.inert = true;
      });
    } else {
      busyElements.forEach((wasInert, element) => { element.inert = wasInert; });
      busyElements.clear();
    }
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  function run(task, { recover = false } = {}) {
    pending += 1;
    setBusy();
    const result = queue.then(async () => {
      // A timed-out write may have reached Shopify. Do not blindly send another write.
      if (failure && !(recover && !failure.cartSafetyUncertain)) throw failure;
      const value = await task();
      if (recover) {
        failure = null;
        const box = document.getElementById('3dmark-cart-safety-message');
        if (box) box.hidden = true;
      }
      return value;
    });
    queue = result.catch(fail).finally(() => {
      pending -= 1;
      setBusy();
    });
    return result;
  }

  async function cartFetch(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const body = await response.text();
      return { ok: response.ok, status: response.status, json: async () => {
        try { return JSON.parse(body); }
        catch (error) { error.cartSafetyUncertain = true; throw error; }
      } };
    } catch (error) {
      error.cartSafetyUncertain = true;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  window.threeDMarkCartSafety = { run, fail, fetch: cartFetch, message };

  function getCartUrl() {
    if (window.theme && theme.routes && theme.routes.cart) {
      return theme.routes.cart.replace(/\/$/, '');
    }

    return '/cart';
  }

  function getCartJsonUrl() {
    return `${getCartUrl()}.js`;
  }

  function fetchCart() {
    return cartFetch(getCartJsonUrl(), {
      headers: {
        Accept: 'application/json'
      }
    }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  async function updateCart(updates) {
    let cart = await fetchCart();
    const originalItems = cart.items;
    const identity = (item) => JSON.stringify([
      item.variant_id,
      Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
      item.selling_plan_allocation?.selling_plan?.id || null
    ]);
    // change.js validates inventory; update.js can overfill existing cart lines.
    // Use fresh line keys after every mutation because discounts can change keys.
    for (const [key, quantity] of Object.entries(updates)) {
      const original = originalItems.find((item) => item.key === key);
      if (!original) throw new Error(message('failed'));
      const matches = cart.items.filter((item) => identity(item) === identity(original));
      const item = cart.items.find((entry) => entry.key === key) || (matches.length === 1 ? matches[0] : null);
      if (!item) throw new Error(message('failed'));
      const response = await cartFetch(`${getCartUrl()}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity })
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || data.message || message('pack'));
        error.cartSafetyKind = 'pack';
        throw error;
      }
      cart = data;
      const remaining = cart.items.filter((entry) => identity(entry) === identity(original));
      const actual = remaining.reduce((total, entry) => total + entry.quantity, 0);
      if (actual !== quantity) {
        const error = new Error(message('pack'));
        error.cartSafetyKind = 'pack';
        throw error;
      }
    }
    return cart;
  }

  function getDomRow(item) {
    if (!window.CSS || !CSS.escape || !item || !item.key) return null;

    return document.querySelector(
      `[data-line-key="${CSS.escape(item.key)}"]`
    );
  }

  function getItemProperty(item, key) {
    if (!item) return '';

    if (item.properties) {
      if (
        item.properties[key] !== undefined &&
        item.properties[key] !== null &&
        item.properties[key] !== ''
      ) {
        return item.properties[key];
      }

      /**
       * 舊版公開 properties fallback
       * 保留相容性，但新版不依賴這些欄位。
       */
      if (
        key === '_parent_line_key' &&
        item.properties['Parent line key']
      ) {
        return item.properties['Parent line key'];
      }

      if (
        key === '_parent_product_id' &&
        item.properties['Parent product id']
      ) {
        return item.properties['Parent product id'];
      }

      if (
        key === '_parent_variant_id' &&
        item.properties['Parent variant id']
      ) {
        return item.properties['Parent variant id'];
      }

      if (
        key === '_addon_variant_id' &&
        item.properties['Addon variant id']
      ) {
        return item.properties['Addon variant id'];
      }

      if (
        key === '_addon_group' &&
        item.properties['Addon group']
      ) {
        return item.properties['Addon group'];
      }
    }

    const row = getDomRow(item);

    if (!row) return '';

    if (key === '_parent_line_key') {
      return row.dataset.addonParentLineKey || '';
    }

    if (key === '_parent_product_id') {
      return row.dataset.addonParentProductId || '';
    }

    if (key === '_parent_variant_id') {
      return row.dataset.addonParentVariantId || '';
    }

    if (key === '_parent_bundle_id') {
      return row.dataset.addonParentBundleId || '';
    }

    if (key === '_addon_group') {
      return row.dataset.addonGroup || '';
    }

    if (key === '_addon_only') {
      return row.dataset.addonOnly === 'true' ? 'true' : '';
    }

    if (key === '_addon_pack_quantity') {
      return row.dataset.addonPackQuantity || '';
    }

    return '';
  }

  function isAddonByProperty(item) {
    return getItemProperty(item, '_addon_only') === 'true';
  }

  function isAddonByDom(item) {
    const row = getDomRow(item);

    return Boolean(
      row &&
      row.dataset.addonOnly === 'true'
    );
  }

  function isAddonItem(item) {
    return isAddonByProperty(item) || isAddonByDom(item);
  }

  function getParentBundleId(item) {
    return String(
      getItemProperty(item, '_parent_bundle_id') || ''
    );
  }

  function getMainBundleId(item) {
    if (!item || !item.properties) return '';

    return String(
      item.properties._3dmark_bundle_id || ''
    );
  }

  function getParentLineKey(item) {
    return String(
      getItemProperty(item, '_parent_line_key') || ''
    );
  }

  function getParentVariantId(item) {
    return String(
      getItemProperty(item, '_parent_variant_id') || ''
    );
  }

  function getParentProductId(item) {
    return String(
      getItemProperty(item, '_parent_product_id') || ''
    );
  }

  function getAddonGroup(item) {
    return String(
      getItemProperty(item, '_addon_group') || ''
    );
  }

  function getAddonPackQuantity(item) {
    const raw =
      getItemProperty(
        item,
        '_addon_pack_quantity'
      );

    const quantity =
      parseInt(raw || '1', 10) || 1;

    return quantity > 0 ? quantity : 1;
  }

  function isPackAddon(item) {
    return getAddonPackQuantity(item) > 1;
  }

  /**
   * 用來判斷同一筆普通加購是否重複。
   *
   * Pack 不使用這個方式刪除，
   * 因為 Pack 同一商品可能合法存在多個 Variant。
   */
  function getAddonGroupKey(item) {
    const addonGroup = getAddonGroup(item);
    const addonVariantId = String(item.variant_id || '');
    const parentBundleId = getParentBundleId(item);
    const parentLineKey = getParentLineKey(item);
    const parentProductId = getParentProductId(item);
    const parentVariantId = getParentVariantId(item);

    if (addonGroup) {
      return addonGroup;
    }

    if (parentBundleId) {
      return `${parentBundleId}-${addonVariantId}`;
    }

    if (parentLineKey) {
      return `${parentLineKey}-${addonVariantId}`;
    }

    if (parentProductId && parentVariantId) {
      return `${parentProductId}-${parentVariantId}-${addonVariantId}`;
    }

    if (parentVariantId) {
      return `${parentVariantId}-${addonVariantId}`;
    }

    if (parentProductId) {
      return `${parentProductId}-${addonVariantId}`;
    }

    return `no-parent-${addonVariantId}`;
  }

  /**
   * Pack 分組。
   *
   * 同一個 parent bundle 下，
   * 所有 _addon_pack_quantity 相同的 Pack 商品
   * 視為同一組。
   */
    function getPackGroupKey(item) {
    const parentBundleId = getParentBundleId(item);
    const parentLineKey = getParentLineKey(item);
    const parentVariantId = getParentVariantId(item);
    const parentProductId = getParentProductId(item);

    const addonProductId = String(item.product_id || '');
    const packQuantity = getAddonPackQuantity(item);

    /**
     * Pack 分組一定要包含 addon product id。
     *
     * 例如同一台主商品：
     * PLA Bundle 4件
     * PETG Bundle 4件
     *
     * 不能被當成同一組。
     */
    if (parentBundleId) {
      return `${parentBundleId}-addon-${addonProductId}-pack-${packQuantity}`;
    }

    if (parentLineKey) {
      return `${parentLineKey}-addon-${addonProductId}-pack-${packQuantity}`;
    }

    if (parentProductId && parentVariantId) {
      return `${parentProductId}-${parentVariantId}-addon-${addonProductId}-pack-${packQuantity}`;
    }

    if (parentVariantId) {
      return `${parentVariantId}-addon-${addonProductId}-pack-${packQuantity}`;
    }

    if (parentProductId) {
      return `${parentProductId}-addon-${addonProductId}-pack-${packQuantity}`;
    }

    return `no-parent-addon-${addonProductId}-pack-${packQuantity}`;
  }       

  function hasAnyParentData(item) {
    return Boolean(
      getParentBundleId(item) ||
      getParentLineKey(item) ||
      getParentVariantId(item) ||
      getParentProductId(item)
    );
  }

  /**
   * 找主商品。
   *
   * 優先順序：
   * 1. bundle id
   * 2. line key
   * 3. variant id
   * 4. product id
   */
  function findParentItem(addonItem, mainItems) {
    const parentBundleId =
      getParentBundleId(addonItem);

    const parentLineKey =
      getParentLineKey(addonItem);

    const parentVariantId =
      getParentVariantId(addonItem);

    const parentProductId =
      getParentProductId(addonItem);

    /**
     * 新版最優先：
     * 主商品 _3dmark_bundle_id
     * 對應加購品 _parent_bundle_id
     */
    if (parentBundleId) {
      const byBundle =
        mainItems.find((mainItem) => {
          return (
            getMainBundleId(mainItem) ===
            parentBundleId
          );
        });

      if (byBundle) return byBundle;
    }

    /**
     * 第二順位：
     * Shopify line item key
     */
    if (parentLineKey) {
      const byLineKey =
        mainItems.find((mainItem) => {
          return (
            String(mainItem.key) ===
            parentLineKey
          );
        });

      if (byLineKey) return byLineKey;
    }

    /**
     * 以下主要是舊資料 fallback。
     */
    if (parentVariantId) {
      const byVariant =
        mainItems.find((mainItem) => {
          return (
            String(mainItem.variant_id) ===
            parentVariantId
          );
        });

      if (byVariant) return byVariant;
    }

    if (parentProductId) {
      const byProduct =
        mainItems.find((mainItem) => {
          return (
            String(mainItem.product_id) ===
            parentProductId
          );
        });

      if (byProduct) return byProduct;
    }

    return null;
  }

  /**
   * 處理 Pack 總數。
   *
   * 目前規則：
   *
   * Pack Quantity = 4
   *
   * 黑 x2
   * 白 x2
   *
   * 合計 4 → 合法
   *
   * 不會因為主商品數量變成 2，
   * 自動把 Pack 放大成 8。
   *
   * 上限永遠使用 Pack 本身設定的數量。
   */
  function processPackGroups(
    addonItems,
    mainItems,
    updates
  ) {
    const packGroups = new Map();

    /**
     * 先依照：
     *
     * 主商品 +
     * 加購商品 +
     * Pack Quantity
     *
     * 分組。
     */
    addonItems
      .filter(isPackAddon)
      .forEach((addonItem) => {
        const groupKey =
          getPackGroupKey(addonItem);

        if (!packGroups.has(groupKey)) {
          packGroups.set(groupKey, []);
        }

        packGroups
          .get(groupKey)
          .push(addonItem);
      });

    packGroups.forEach((items) => {
      if (!items.length) return;

      const firstItem = items[0];

      const parentItem =
        findParentItem(
          firstItem,
          mainItems
        );

      const hasParentData =
        hasAnyParentData(firstItem);

      /**
       * ============================
       * 主商品不存在
       * ============================
       */

      if (mainItems.length === 0) {
        items.forEach((item) => {
          updates[item.key] = 0;
        });

        return;
      }

      if (
        hasParentData &&
        !parentItem
      ) {
        items.forEach((item) => {
          updates[item.key] = 0;
        });

        return;
      }

      /**
       * 舊資料沒有 parent 資訊，
       * 不自動修改。
       */
      if (!hasParentData) {
        return;
      }

      /**
       * ============================
       * Pack 數量
       * ============================
       */

      const packQuantity =
        getAddonPackQuantity(firstItem);

      const totalPackUnits =
        items.reduce(
          (total, item) => {
            return (
              total +
              Number(item.quantity || 0)
            );
          },
          0
        );

      /**
       * ============================
       * 情況 1：
       * 數量剛好
       * ============================
       *
       * Pack = 4
       * 黑2 + 白2
       *
       * 不處理。
       */
      if (
        totalPackUnits === packQuantity
      ) {
        return;
      }

      /**
       * ============================
       * 情況 2：
       * 數量不足
       * ============================
       *
       * 缺少的數量全部補到
       * 購物車目前第一個 Variant。
       *
       * Pack = 4
       *
       * 黑1
       * 白1
       * 紅1
       *
       * → 黑2
       *   白1
       *   紅1
       */
      if (
        totalPackUnits > 0 &&
        totalPackUnits < packQuantity
      ) {
        const missing =
          packQuantity - totalPackUnits;

        const firstQuantity =
          Number(
            firstItem.quantity || 0
          );

        updates[firstItem.key] =
          firstQuantity + missing;

        console.log(
          '[3DMARK PACK] quantity too low:',
          {
            packQuantity,
            currentQuantity:
              totalPackUnits,
            missing,
            addedTo:
              firstItem.variant_id,
            newQuantity:
              firstQuantity + missing
          }
        );

        return;
      }

      /**
       * ============================
       * 情況 3：
       * 數量超過 Pack
       * ============================
       *
       * 從最後一筆開始扣。
       *
       * 例如：
       *
       * Pack = 4
       *
       * 黑2
       * 白2
       * 紅1
       *
       * 總數5
       *
       * → 從紅開始扣1
       */
      if (
        totalPackUnits > packQuantity
      ) {
        let excess =
          totalPackUnits - packQuantity;

        const reversedItems =
          [...items].reverse();

        reversedItems.forEach((item) => {
          if (excess <= 0) return;

          const currentQuantity =
            Number(
              item.quantity || 0
            );

          if (currentQuantity <= 0) {
            return;
          }

          /**
           * 整筆都要移除。
           */
          if (
            currentQuantity <= excess
          ) {
            updates[item.key] = 0;

            excess -=
              currentQuantity;

            return;
          }

          /**
           * 只需要減少部分數量。
           */
          updates[item.key] =
            currentQuantity - excess;

          excess = 0;
        });

        console.log(
          '[3DMARK PACK] quantity too high:',
          {
            packQuantity,
            currentQuantity:
              totalPackUnits
          }
        );
      }
    });
  }

  function guardAddonCart(options = {}) {
    if (checkingOut && !options.checkout) return Promise.resolve(false);
    return run(() => performGuardAddonCart(options)).catch((error) => {
      fail(error);
      if (options.checkout) throw error;
      return false;
    });
  }

  async function performGuardAddonCart(options = {}) {
    try {
      /**
       * 加入商品後短暫暫停 Guard。
       *
       * 刪除 / 修改購物車時
       * ignorePause: true 仍會強制執行。
       */
      if (
        !options.ignorePause &&
        window.threeDMarkAddonCartGuardPausedUntil &&
        Date.now() <
          window.threeDMarkAddonCartGuardPausedUntil
      ) {
        return false;
      }

      const cart =
        await fetchCart();

      const updates = {};

      const seenGroups =
        new Set();

      const addonItems =
        cart.items.filter(
          isAddonItem
        );

      const mainItems =
        cart.items.filter(
          (item) =>
            !isAddonItem(item)
        );

      /**
       * ============================
       * 第一階段：
       * 一般加購品
       * ============================
       */
      addonItems
        .filter(
          (item) =>
            !isPackAddon(item)
        )
        .forEach((addonItem) => {
          const groupKey =
            getAddonGroupKey(
              addonItem
            );

          const parentItem =
            findParentItem(
              addonItem,
              mainItems
            );

          const hasParentData =
            hasAnyParentData(
              addonItem
            );

          /**
           * 沒任何主商品。
           */
          if (
            mainItems.length === 0
          ) {
            updates[
              addonItem.key
            ] = 0;

            return;
          }

          /**
           * 有 parent 資料，
           * 但主商品已刪除。
           */
          if (
            hasParentData &&
            !parentItem
          ) {
            updates[
              addonItem.key
            ] = 0;

            return;
          }

          /**
           * 舊資料無 parent：
           * 暫時不動。
           */
          if (!hasParentData) {
            return;
          }

          /**
           * 同一主商品 +
           * 同一普通加購 Variant
           * 不保留重複 line item。
           */
          if (
            seenGroups.has(
              groupKey
            )
          ) {
            updates[
              addonItem.key
            ] = 0;

            return;
          }

          seenGroups.add(
            groupKey
          );

          /**
           * 普通加購數量規則：
           *
           * 維持原本邏輯：
           * 不得高於主商品數量。
           *
           * 主商品 2
           * 加購 1 → OK
           * 加購 2 → OK
           * 加購 3 → 降為 2
           */
          if (
            parentItem &&
            addonItem.quantity >
              parentItem.quantity
          ) {
            updates[
              addonItem.key
            ] =
              parentItem.quantity;
          }
        });

      /**
       * ============================
       * 第二階段：
       * Pack 加購品
       * ============================
       */
      processPackGroups(
        addonItems,
        mainItems,
        updates
      );

      if (
        Object.keys(updates)
          .length === 0
      ) {
        if (options.checkout) {
          const displayed = Array.from(document.querySelectorAll('cart-items input[name^="updates["]'))
            .map((input) => [input.name.slice(8, -1), Number(input.value)])
            .sort(([a], [b]) => a.localeCompare(b));
          const actual = cart.items.map((item) => [item.key, item.quantity])
            .sort(([a], [b]) => a.localeCompare(b));
          if (JSON.stringify(displayed) !== JSON.stringify(actual)) {
            await refreshCartUI(cart);
            reviewRequired = true;
            return true;
          }
        }
        return false;
      }

      console.log(
        '[3DMARK ADDON CART GUARD] updates:',
        updates
      );

      const updatedCart = await updateCart(updates);
      reviewRequired = true;

      if (
        options.refresh !== false
      ) {
        await refreshCartUI(updatedCart);
      }

      return true;
    } catch (error) {
      console.warn(
        '[3DMARK ADDON CART GUARD] failed:',
        error
      );

      throw error;
    }
  }

  async function refreshCartUI(cart) {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      await cartItems.refresh(cart);
      setBusy();
      showMessage('updated');
    }
  }

  let guardTimer = null;

  function scheduleGuard(
    delayMs,
    options = {}
  ) {
    clearTimeout(guardTimer);

    guardTimer =
      setTimeout(() => {
        guardAddonCart(
          options
        );
      }, delayMs || 2000);
  }

  /**
   * 頁面載入後檢查一次。
   */
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      setTimeout(() => {
        guardAddonCart();
      }, 3000);
    }
  );

  /**
   * 刪除商品。
   *
   * 主商品刪除後，
   * 對應普通加購與整個 Pack
   * 都會一起清掉。
   */
  document.addEventListener(
    'click',
    (event) => {
      if (
        event.target.closest(
          '.js-remove-item'
        ) ||
        event.target.closest(
          'cart-remove-button'
        ) ||
        event.target.closest(
          '[href*="/cart/change"]'
        )
      ) {
        scheduleGuard(
          1200,
          {
            ignorePause: true
          }
        );

        setTimeout(() => {
          guardAddonCart({
            ignorePause: true
          });
        }, 2500);
      }
    }
  );

  /**
   * cart-items.js 更新數量後。
   */
  document.addEventListener(
    '3dmark:addon-cart-check',
    () => {
      scheduleGuard(
        1200,
        {
          ignorePause: true
        }
      );
    }
  );

  document.addEventListener(
    'cart:updated',
    () => {
      scheduleGuard(2500);
    }
  );

  /**
   * 新增商品完成。
   *
   * 延後檢查，
   * 避免和加入 Pack 過程衝突。
   */
  document.addEventListener(
    'on:cart:add',
    () => {
      scheduleGuard(7000);
    }
  );

  /**
   * 修改 line item。
   */
  document.addEventListener(
    'on:line-item:change',
    () => {
      scheduleGuard(
        1200,
        {
          ignorePause: true
        }
      );
    }
  );

  // Bubble phase preserves the existing cart-terms and app cancellation handlers.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form || !['cart', 'cart-drawer-form'].includes(form.id)) return;
    if (!event.submitter || event.submitter.name !== 'checkout') return;
    if (allowSubmit === form) {
      allowSubmit = null;
      submittedEvent = event;
      return;
    }
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    clearTimeout(guardTimer);
    setBusy();
    let navigating = false;
    const formId = form.id;
    const note = form.elements.namedItem('note')?.value;
    try {
      await queue;
      if (failure) throw failure;
      // Product-page add-on flows set a pause while assembling their bundle.
      // Do not validate or submit a potentially half-built bundle.
      if (Date.now() < (window.threeDMarkAddonCartGuardPausedUntil || 0)) {
        showMessage('updated');
        return;
      }
      if (reviewRequired) {
        reviewRequired = false;
        showMessage('updated');
        return;
      }
      const changed = await guardAddonCart({ checkout: true, ignorePause: true });
      if (failure) throw failure;
      if (changed) {
        reviewRequired = false;
        showMessage('updated');
        return;
      }
      const currentForm = document.getElementById(formId);
      if (!currentForm) throw new Error(message('failed'));
      const button = Array.from(currentForm.elements).find((element) => element.name === 'checkout');
      if (!button || button.disabled) throw new Error(message('failed'));
      if (note !== undefined && currentForm.elements.namedItem('note')) {
        currentForm.elements.namedItem('note').value = note;
      }
      submittedEvent = null;
      allowSubmit = currentForm;
      currentForm.requestSubmit(button);
      allowSubmit = null;
      navigating = Boolean(submittedEvent && !submittedEvent.defaultPrevented);
    } catch (error) {
      fail(error);
    } finally {
      checkingOut = navigating;
      setBusy();
    }
  });

  // Restore controls when returning from checkout using the browser Back button.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    checkingOut = false;
    allowSubmit = null;
    setBusy();
  });

  window.threeDMarkAddonCartGuard =
    guardAddonCart;
})();